// User Role Definitions and Permissions System

export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin', 
  TESTER: 'tester',
  MEMBER: 'member'
};

export const PERMISSIONS = {
  // Admin Debug Panel Access
  ACCESS_DEBUG_PANEL: 'access_debug_panel',
  VIEW_ALL_PROFILES: 'view_all_profiles',
  VIEW_SYSTEM_LOGS: 'view_system_logs',
  
  // User Management
  MANAGE_USERS: 'manage_users',
  DELETE_USERS: 'delete_users',
  CHANGE_USER_ROLES: 'change_user_roles',
  
  // Content Management
  MODERATE_CONTENT: 'moderate_content',
  DELETE_ANY_CONTENT: 'delete_any_content',
  
  // System Administration
  MANAGE_SYSTEM_SETTINGS: 'manage_system_settings',
  VIEW_ANALYTICS: 'view_analytics',
  EXPORT_DATA: 'export_data',
  
  // Feedback and Testing
  SUBMIT_FEEDBACK: 'submit_feedback',
  VIEW_FEEDBACK_PANEL: 'view_feedback_panel',
  
  // Basic User Permissions
  CREATE_CONTENT: 'create_content',
  EDIT_OWN_PROFILE: 'edit_own_profile',
  DELETE_OWN_CONTENT: 'delete_own_content'
};

// Role-Permission Mapping
export const ROLE_PERMISSIONS = {
  [USER_ROLES.SUPER_ADMIN]: [
    // Super Admin has ALL permissions
    ...Object.values(PERMISSIONS)
  ],
  
  [USER_ROLES.ADMIN]: [
    // Admin permissions (everything except super admin exclusive actions)
    PERMISSIONS.ACCESS_DEBUG_PANEL,
    PERMISSIONS.VIEW_ALL_PROFILES,
    PERMISSIONS.VIEW_SYSTEM_LOGS,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.CHANGE_USER_ROLES, // Can promote to admin but not super_admin
    PERMISSIONS.MODERATE_CONTENT,
    PERMISSIONS.DELETE_ANY_CONTENT,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.EXPORT_DATA,
    PERMISSIONS.SUBMIT_FEEDBACK,
    PERMISSIONS.VIEW_FEEDBACK_PANEL,
    PERMISSIONS.CREATE_CONTENT,
    PERMISSIONS.EDIT_OWN_PROFILE,
    PERMISSIONS.DELETE_OWN_CONTENT
  ],
  
  [USER_ROLES.TESTER]: [
    // Tester permissions (member + feedback capabilities)
    PERMISSIONS.SUBMIT_FEEDBACK,
    PERMISSIONS.VIEW_FEEDBACK_PANEL,
    PERMISSIONS.CREATE_CONTENT,
    PERMISSIONS.EDIT_OWN_PROFILE,
    PERMISSIONS.DELETE_OWN_CONTENT
  ],
  
  [USER_ROLES.MEMBER]: [
    // Basic user permissions
    PERMISSIONS.CREATE_CONTENT,
    PERMISSIONS.EDIT_OWN_PROFILE,
    PERMISSIONS.DELETE_OWN_CONTENT
  ]
};

// Helper Functions
export const hasPermission = (userRole, permission) => {
  if (!userRole || !permission) return false;
  const rolePermissions = ROLE_PERMISSIONS[userRole] || [];
  return rolePermissions.includes(permission);
};

export const hasAnyPermission = (userRole, permissions) => {
  if (!userRole || !permissions || !Array.isArray(permissions)) return false;
  return permissions.some(permission => hasPermission(userRole, permission));
};

export const hasAllPermissions = (userRole, permissions) => {
  if (!userRole || !permissions || !Array.isArray(permissions)) return false;
  return permissions.every(permission => hasPermission(userRole, permission));
};

export const isAdmin = (userRole) => {
  return userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPER_ADMIN;
};

export const isSuperAdmin = (userRole) => {
  return userRole === USER_ROLES.SUPER_ADMIN;
};

export const isTester = (userRole) => {
  return userRole === USER_ROLES.TESTER;
};

export const canAccessDebugPanel = (userRole) => {
  return hasPermission(userRole, PERMISSIONS.ACCESS_DEBUG_PANEL);
};

export const canAccessFeedbackPanel = (userRole) => {
  return hasPermission(userRole, PERMISSIONS.VIEW_FEEDBACK_PANEL);
};

export const canSubmitFeedback = (userRole) => {
  return hasPermission(userRole, PERMISSIONS.SUBMIT_FEEDBACK);
};

export const canManageUsers = (userRole) => {
  return hasPermission(userRole, PERMISSIONS.MANAGE_USERS);
};

export const canChangeUserRole = (currentUserRole, targetRole) => {
  // Super admins can change anyone to any role
  if (currentUserRole === USER_ROLES.SUPER_ADMIN) return true;
  
  // Admins can promote to admin, tester, or member but not super_admin
  if (currentUserRole === USER_ROLES.ADMIN && targetRole !== USER_ROLES.SUPER_ADMIN) return true;
  
  // Members and testers can't change roles
  return false;
};

// Role Display Names
export const ROLE_DISPLAY_NAMES = {
  [USER_ROLES.SUPER_ADMIN]: 'Super Administrator',
  [USER_ROLES.ADMIN]: 'Administrator',
  [USER_ROLES.TESTER]: 'Tester',
  [USER_ROLES.MEMBER]: 'Member'
};

export const getRoleDisplayName = (role) => {
  return ROLE_DISPLAY_NAMES[role] || 'Unknown Role';
};

// Role Colors for UI
export const ROLE_COLORS = {
  [USER_ROLES.SUPER_ADMIN]: '#ff4444', // Red
  [USER_ROLES.ADMIN]: '#ff8800',       // Orange
  [USER_ROLES.TESTER]: '#8b5cf6',      // Purple
  [USER_ROLES.MEMBER]: '#4CAF50'       // Green
};

export const getRoleColor = (role) => {
  return ROLE_COLORS[role] || '#666666';
};

// Validation
export const isValidRole = (role) => {
  return Object.values(USER_ROLES).includes(role);
};

export const getAvailableRoles = (currentUserRole) => {
  if (currentUserRole === USER_ROLES.SUPER_ADMIN) {
    return Object.values(USER_ROLES);
  } else if (currentUserRole === USER_ROLES.ADMIN) {
    return [USER_ROLES.MEMBER, USER_ROLES.TESTER, USER_ROLES.ADMIN];
  } else {
    return [USER_ROLES.MEMBER];
  }
}; 